import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'phosphor-react';
import { COLORS } from '../../../utils/colors';
import { AppButton } from '../AppButton/AppButton';
import { VerifiedJobDataPick } from './SupplierVerifyJobForm';
import { SupplierJobAddressDetails, areArraysOfObjectsEqualUnordered } from '@dill/dill-shared';

const SupplierCompareJob = ({
  onClose,
  verifyJobData,
  originalJobData,
  onReviewData,
}: {
  onClose: () => void;
  verifyJobData: VerifiedJobDataPick;
  originalJobData: VerifiedJobDataPick;
  onReviewData: (data: VerifiedJobDataPick) => void;
}) => {
  const [formState, setFormState] = useState<any>({});
  const [formOwnerAddressState, setFormOwnerAddressState] = useState<
    { name: string; address: string; city: string; state: string; zipCode: string }[]
  >([]);
  const [formGCAddressState, setFormGCAddressState] = useState<
    { name: string; address: string; city: string; state: string; zipCode: string }[]
  >([]);

  const onChange2 = (data: { name: string; value: string | number }) => {
    setFormState((prevState: any) => ({
      ...prevState,
      [data.name]: data.value,
    }));
  };

  const handleReviewData = () => {
    let moreDetails = {};
    if (formOwnerAddressState.length > 0) {
      moreDetails = {
        ...moreDetails,
        owners: formOwnerAddressState,
      };
    }
    if (formGCAddressState.length > 0) {
      moreDetails = {
        ...moreDetails,
        GCs: formGCAddressState,
      };
    }
    onReviewData({ ...originalJobData, ...formState, ...moreDetails });
  };

  useEffect(() => {
    const updatedState: Record<string, string> = {};
    Object.keys(verifyJobData).forEach((key) => {
      if (
        verifyJobData[key as keyof VerifiedJobDataPick] !==
          originalJobData[key as keyof VerifiedJobDataPick] &&
        verifyJobData[key as keyof VerifiedJobDataPick]?.toString().trim() !== ''
      ) {
        updatedState[key] = verifyJobData[key as keyof VerifiedJobDataPick] as string;
      }
    });

    setFormState(updatedState);
    setFormOwnerAddressState(verifyJobData.owners?.filter((o) => o.name !== '') ?? []);
    setFormGCAddressState(verifyJobData.GCs?.filter((o) => o.name !== '') ?? []);
  }, [verifyJobData, originalJobData]);

  const Option = ({
    label,
    verifiedData,
    originalData,
    onSelect,
  }: {
    label: string;
    verifiedData: { value: string; name: string };
    originalData: { value: string; name: string };
    onSelect: (data: { name: string; value: string | number }) => void;
  }) => {
    const hideRadioButton = () => {
      return verifiedData.value !== originalData.value && verifiedData.value.trim() !== '';
    };
    return (
      <div className="p-3 w-full flex">
        <div className="text-xs mb-1 font-semibold w-1/3">{label}</div>
        <div className="flex  items-center w-1/3">
          {hideRadioButton() && (
            <div
              className="flex items-center mr-1 cursor-pointer"
              onClick={() => onSelect({ name: verifiedData.name, value: verifiedData.value })}>
              <div
                className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                  formState[verifiedData.name] === verifiedData.value
                    ? 'PRIMARY_500-BORDER'
                    : 'GREY_500-BORDER'
                }`}>
                <div
                  className={`flex items-center h-2 w-2 rounded-full ${
                    formState[verifiedData.name] === verifiedData.value ? 'PRIMARY_500-BG ' : ''
                  }`}></div>
              </div>
            </div>
          )}
          <div className="text-xs TEXT_SECONDARY-CLR ">{verifiedData.value}</div>
        </div>
        <div className="flex  items-center w-1/3">
          {hideRadioButton() && (
            <div
              className="flex items-center mr-1 cursor-pointer"
              onClick={() => onSelect({ name: originalData.name, value: originalData.value })}>
              <div
                className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                  formState[originalData.name] === originalData.value
                    ? 'PRIMARY_500-BORDER'
                    : 'GREY_500-BORDER'
                }`}>
                <div
                  className={`flex items-center h-2 w-2 rounded-full ${
                    formState[originalData.name] === originalData.value ? 'PRIMARY_500-BG ' : ''
                  }`}></div>
              </div>
            </div>
          )}
          <div className="text-xs TEXT_SECONDARY-CLR ">{originalData.value}</div>
        </div>
      </div>
    );
  };

  const CompareArrayOption = ({
    label,
    verifiedData,
    originalData,
    onSelect,
    selectedData,
  }: {
    label: string;
    verifiedData: { name: string; address: string; city: string; state: string; zipCode: string }[];
    originalData: { name: string; address: string; city: string; state: string; zipCode: string }[];
    onSelect: (
      data: { name: string; address: string; city: string; state: string; zipCode: string }[]
    ) => void;
    selectedData: { name: string; address: string; city: string; state: string; zipCode: string }[];
  }) => {
    const hideRadioButton = () => {
      return !areArraysOfObjectsEqualUnordered(verifiedData, originalData);
    };
    return (
      <div className="p-3 w-full flex">
        <div className="text-xs mb-1 font-semibold w-1/3">{label}</div>
        <div className="flex w-1/3 h-fit">
          {hideRadioButton() && (
            <div className="flex mt-2 mr-1 cursor-pointer" onClick={() => onSelect(verifiedData)}>
              <div
                className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                  areArraysOfObjectsEqualUnordered(verifiedData, selectedData)
                    ? 'PRIMARY_500-BORDER'
                    : 'GREY_500-BORDER'
                }`}>
                <div
                  className={`flex items-center h-2 w-2 rounded-full ${
                    areArraysOfObjectsEqualUnordered(verifiedData, selectedData)
                      ? 'PRIMARY_500-BG '
                      : ''
                  }`}></div>
              </div>
            </div>
          )}
          <div className="text-xs TEXT_SECONDARY-CLR border GREY_300-BORDER  rounded-[8px] px-2 w-full mr-1">
            {verifiedData.map((data, i) => (
              <div key={`verify_owner_job_${i}`}>
                {data.name && (
                  <div className="items-center w-full border-b GREY_300-BORDER py-2">
                    <div className="text-xs TEXT_SECONDARY-CLR font-semibold">{data.name}</div>
                    <div className="text-xs TEXT_SECONDARY-CLR ">{`${data.address ?? ''}, ${
                      data.city ?? ''
                    }, ${data.state ?? ''} ${data.zipCode ?? ''}`}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-1/3 h-fit">
          {hideRadioButton() && (
            <div className="flex mt-2 mr-1 cursor-pointer" onClick={() => onSelect(originalData)}>
              <div
                className={`flex items-center justify-center h-4 w-4 border-2 rounded-full ${
                  areArraysOfObjectsEqualUnordered(originalData, selectedData)
                    ? 'PRIMARY_500-BORDER'
                    : 'GREY_500-BORDER'
                }`}>
                <div
                  className={`flex items-center h-2 w-2 rounded-full ${
                    areArraysOfObjectsEqualUnordered(originalData, selectedData)
                      ? 'PRIMARY_500-BG '
                      : ''
                  }`}></div>
              </div>
            </div>
          )}
          <div className="text-xs TEXT_SECONDARY-CLR border GREY_300-BORDER  rounded-[8px] px-2 w-full">
            {originalData.map((data, i) => (
              <div key={`verify_owner_job_${i}`}>
                {data.name && (
                  <div className="items-center w-full border-b GREY_300-BORDER py-2">
                    <div className="text-xs TEXT_SECONDARY-CLR font-semibold">{data.name}</div>
                    <div className="text-xs TEXT_SECONDARY-CLR ">{`${data.address ?? ''}, ${
                      data.city ?? ''
                    }, ${data.state ?? ''} ${data.zipCode ?? ''}`}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`new-job-modal-content pt-1`}>
      <div className="new-job-modal-title-container">
        <h3 className="font-medium text-lg">{`Compare Data`}</h3>
        <AppButton
          type="FLAT"
          buttonStyles={{ padding: '0px', margin: '5px 2px' }}
          icon={<X color={COLORS.GREY_300} size={30} />}
          onClick={onClose}
        />
      </div>
      <div className="p-3">
        <div className="text-xs my-2">Please choose which data you want added</div>

        <div className="flex flex-col h-full  w-full border rounded-lg overflow-hidden">
          <div className="flex w-full h-full flex-col">
            <div className="flex border-b px-2 items-center w-full sticky top-0 GREY_50-BG py-3 z-10">
              <div className="flex w-1/3 text-xs TEXT_SECONDARY-CLR ">Name</div>
              <div className="flex w-1/3 text-xs TEXT_SECONDARY-CLR ">Verified</div>
              <div className="flex w-1/3 text-xs TEXT_SECONDARY-CLR ">Your Data</div>
            </div>
          </div>
          <div className="h-[calc(90vh-200px)] !overflow-y-auto">
            <Option
              label="Job Name"
              verifiedData={{ value: verifyJobData.name ?? '', name: 'name' }}
              originalData={{ value: originalJobData.name ?? '', name: 'name' }}
              onSelect={onChange2}
            />
            <Option
              label="Job Number"
              verifiedData={{ value: verifyJobData.jobNumber ?? '', name: 'jobNumber' }}
              originalData={{ value: originalJobData.jobNumber ?? '', name: 'jobNumber' }}
              onSelect={onChange2}
            />
            <Option
              label="Job Address"
              verifiedData={{ value: verifyJobData.jobAddress ?? '', name: 'jobAddress' }}
              originalData={{ value: originalJobData.jobAddress ?? '', name: 'jobAddress' }}
              onSelect={onChange2}
            />
            <Option
              label="Job City"
              verifiedData={{ value: verifyJobData.jobCity ?? '', name: 'jobCity' }}
              originalData={{ value: originalJobData.jobCity ?? '', name: 'jobCity' }}
              onSelect={onChange2}
            />
            <Option
              label="Job State"
              verifiedData={{ value: verifyJobData.jobState ?? '', name: 'jobState' }}
              originalData={{ value: originalJobData.jobState ?? '', name: 'jobState' }}
              onSelect={onChange2}
            />
            <Option
              label="Job Type"
              verifiedData={{ value: verifyJobData.jobType ?? '', name: 'jobType' }}
              originalData={{ value: originalJobData.jobType ?? '', name: 'jobType' }}
              onSelect={onChange2}
            />
            <Option
              label="Property APN"
              verifiedData={{ value: verifyJobData.apn ?? '', name: 'apn' }}
              originalData={{ value: originalJobData.apn ?? '', name: 'apn' }}
              onSelect={onChange2}
            />
            {verifyJobData.owners &&
              verifyJobData.owners.filter((o) => o.name !== '').length > 0 && (
                <CompareArrayOption
                  label="Owners"
                  originalData={originalJobData.owners ?? []}
                  verifiedData={verifyJobData.owners ?? []}
                  selectedData={formOwnerAddressState}
                  onSelect={setFormOwnerAddressState}
                />
              )}
            {verifyJobData.GCs && verifyJobData.GCs.length > 0 && (
              <CompareArrayOption
                label="GCs"
                originalData={originalJobData.GCs ?? []}
                verifiedData={verifyJobData.GCs ?? []}
                selectedData={formGCAddressState}
                onSelect={setFormGCAddressState}
              />
            )}
          </div>
        </div>
      </div>

      <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
      <div className="flex flex-row self-end px-6 gap-3 mb-2">
        <AppButton text={'Close'} type="TERTIARY" onClick={onClose} />
        <AppButton text="Review" type="PRIMARY" onClick={handleReviewData} />
      </div>
    </div>
  );
};

export default SupplierCompareJob;
